import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Button,
  Form,
  Card,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useHttpClient } from "../hooks/http-hook";
import { useAuth } from "../Context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next"; // Importer useTranslation

const Profile = () => {
  const { sendRequest } = useHttpClient();
  const { authData } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(); // Assigner useTranslation à {t}

  const [user, setUser] = useState({
    first_name: "",
    last_name: "",
    email: "",
    role: "",
    twoFactorEnabled: false,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [errorMessages, setErrorMessages] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false); // État pour la vérification
  const [verificationCode, setVerificationCode] = useState(""); // Code de vérification

  useEffect(() => {
    const fetchUser = async () => {
      if (!authData.userId) return;
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}users/${authData.userId}`
        );
        if (response && response.user) {
          setUser({
            first_name: response.user.first_name,
            last_name: response.user.last_name,
            email: response.user.email,
            role: response.user.role,
            twoFactorEnabled: response.user.twoFactorEnabled,
          });
        }
      } catch (err) {
        console.log(err);
      }
    };
    fetchUser();
  }, [authData.userId, sendRequest]);

  const handleEnable2FA = async () => {
    setIsLoading(true);
    setErrorMessages([]);
    setSuccessMessage("");

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/auth/2fa/enable`,
        "POST",
        null,
        { "Content-Type": "application/json" }
      );

      setIsVerifying(true); // Passer à l'état de vérification
      setSuccessMessage(response.message || t("2fa_verification_code_sent"));
    } catch (err) {
      setErrorMessages([err.message || t("2fa_enable_error")]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    setIsLoading(true);
    setErrorMessages([]);
    setSuccessMessage("");

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/auth/2fa/verify-enable`,
        "POST",
        JSON.stringify({ code: verificationCode }),
        { "Content-Type": "application/json" }
      );

      setUser((prevUser) => ({ ...prevUser, twoFactorEnabled: true }));
      setSuccessMessage(response.message || t("2fa_enabled_success"));
      setIsVerifying(false); // Quitter l'état de vérification
    } catch (err) {
      setErrorMessages([err.message || t("2fa_verify_error")]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDisable2FA = async () => {
    setIsLoading(true);
    setErrorMessages([]);
    setSuccessMessage("");

    try {
      const response = await sendRequest(
        `${process.env.REACT_APP_BACKEND_URL}/auth/2fa/disable`,
        "POST",
        null,
        { "Content-Type": "application/json" }
      );

      setUser((prevUser) => ({ ...prevUser, twoFactorEnabled: false }));
      setSuccessMessage(response.message || t("2fa_disabled_success"));
    } catch (err) {
      setErrorMessages([err.message || t("2fa_disable_error")]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container className="p-4">
      <Card className="shadow-sm mb-4">
        <Card.Body>
          <h2 className="mb-4">{t("user_profile")}</h2>
          {errorMessages.length > 0 && (
            <Alert variant="danger">{errorMessages[0]}</Alert>
          )}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("first_name")}</Form.Label>
              <Form.Control
                type="text"
                name="first_name"
                value={user.first_name}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("last_name")}</Form.Label>
              <Form.Control
                type="text"
                name="last_name"
                value={user.last_name}
                readOnly
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("email")}</Form.Label>
              <Form.Control type="email" value={user.email} readOnly />
            </Form.Group>
          </Form>
        </Card.Body>
      </Card>
      <Row>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Body>
              <h4>{t("2fa_title")}</h4>
              <p>
                {t("2fa_status")}{" "}
                {user.twoFactorEnabled ? (
                  <span className="text-success">{t("enabled")}</span>
                ) : (
                  <span className="text-danger">{t("disabled")}</span>
                )}
              </p>
              {isVerifying ? (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>{t("verification_code")}</Form.Label>
                    <Form.Control
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    variant="primary"
                    onClick={handleVerify2FA}
                    disabled={isLoading || !verificationCode.trim()}
                  >
                    {isLoading ? t("verifying") : t("verify_code")}
                  </Button>
                </>
              ) : (
                <Button
                  variant={user.twoFactorEnabled ? "danger" : "primary"}
                  onClick={
                    user.twoFactorEnabled ? handleDisable2FA : handleEnable2FA
                  }
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : user.twoFactorEnabled ? (
                    t("disable_2fa")
                  ) : (
                    t("enable_2fa")
                  )}
                </Button>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
